import { SET_CURRENT_CLIENT } from '../../AdviserArea/AdviserAreaActions';
import * as actions from './actions';
import {
  defaultPeriod,
  yesterday,
  defaultTransactionFilters,
} from '../AccountPerformanceReporting.constants';
import { PerformancePeriodDTO, TabSlug } from '../types';

export interface GroupSelection {
  defaultTabIndex: number | null;
  tabList: string[] | null;
  selectedTabIndex: number | null;
  isLoading: boolean | null;
}

export interface WidgetsState {
  performanceGroupSelection: GroupSelection;
  incomeGroupSelection: GroupSelection;
}

const WIDGETS_GROUP_SELECTION_TAB_MAP = {
  [TabSlug.Performance]: 'performanceGroupSelection',
  [TabSlug.Income]: 'incomeGroupSelection',
};

const groupSelectionInitialState = {
  defaultTabIndex: null,
  selectedTabIndex: null,
  tabList: null,
  isLoading: null,
};

const initialState = {
  focusedAccount: {
    accountId: null,
    isLoadingApexApplicants: false,
    apexApplicants: null,
    fetchAccountApplicantsErrors: [],
    updateApexAccountErrors: [],
  },
  accounts: {},
  areOptionsLoaded: false,
  options: {
    performancePeriodOptions: [],
    portfoliosInception: [],
    transactionsSettings: {
      showCostBasis: false,
    },
    defaultPerformancePeriod: 0,
    showGrossOfFeeDisclaimer: false,
  },
  period: defaultPeriod,
  positionsDate: yesterday,
  transactionFilters: defaultTransactionFilters,
  performanceGroupSelection: groupSelectionInitialState,
  incomeGroupSelection: groupSelectionInitialState,
};

export default (state = initialState, { type, payload }) => {
  switch (type) {
    case actions.SET_PERIOD:
      return {
        ...state,
        period: payload,
      };

    case actions.SET_POSITIONS_DATE:
      return {
        ...state,
        positionsDate: payload,
      };

    case SET_CURRENT_CLIENT:
      return initialState;

    case actions.SET_FOCUSED_ACCOUNT_ID: {
      const { accountId } = payload;
      const {
        period,
        options: { portfoliosInception },
      } = state;

      const focusedAccount = state.accounts[accountId] || {
        ...initialState.focusedAccount,
        accountId,
      };

      const isInception = period.label.toLowerCase().includes('inception');
      const portfoliosInceptionList = Object.values(portfoliosInception).flat() as {
        id: string;
        inceptionDate: string;
      }[];

      // When there is no accountId (groupId or accountId) in url params,
      // Need to fetch inception date from `performancePeriodOptions`
      // according to useInceptionDate() hook
      const sinceInceptionPeriodOption: any = [...state.options.performancePeriodOptions].find(
        ({ value }) => value === PerformancePeriodDTO.AllTime,
      );

      // When there is no accountId (groupId or accountId) in url params,
      // Need to Use useInceptionDate() hook logic to derive the Inception Date
      const inceptionDate = accountId
        ? portfoliosInceptionList.find(portfolio => portfolio.id.toString() === accountId)
            ?.inceptionDate
        : sinceInceptionPeriodOption
        ? sinceInceptionPeriodOption.range.startDate
        : null;

      return {
        ...state,
        focusedAccount,
        accounts: {
          ...state.accounts,
          [accountId]: focusedAccount,
        },
        performanceGroupSelection: groupSelectionInitialState,
        incomeGroupSelection: groupSelectionInitialState,
        period: {
          ...state.period,
          dates: [isInception && inceptionDate ? inceptionDate : period.dates[0], period.dates[1]],
        },
      };
    }

    case actions.SET_OPTIONS: {
      const { options } = payload;

      const { defaultPerformancePeriod, portfoliosInception } = options;

      const {
        label,
        value,
        range: { startDate, endDate },
      } = defaultPerformancePeriod;

      const {
        focusedAccount: { accountId },
      } = state;

      const isInception =
        accountId && defaultPerformancePeriod.label.toLowerCase().includes('inception');
      const portfoliosInceptionList = Object.values(portfoliosInception).flat() as {
        id: string;
        inceptionDate: string;
      }[];

      const inceptionDate = portfoliosInceptionList.find(
        portfolio => portfolio.id.toString() === accountId,
      )?.inceptionDate;

      return {
        ...state,
        areOptionsLoaded: true,
        options,
        period: {
          label,
          value,
          dates: [isInception && inceptionDate ? inceptionDate : startDate, endDate],
        },
      };
    }

    case actions.UPDATE_TRANSACTION_FILTERS:
      return { ...state, transactionFilters: { ...state.transactionFilters, ...payload } };

    case actions.GROUP_SELECTION_DEFAULT_TAB_INDEX_SET: {
      const { tabIndex: defaultTabIndex, tabSlug } = payload;

      const segment = WIDGETS_GROUP_SELECTION_TAB_MAP[tabSlug];

      return {
        ...state,
        [segment]: { ...state[segment], defaultTabIndex },
      };
    }
    case actions.GROUP_SELECTION_SELECTED_TAB_INDEX_SET: {
      const { tabIndex: selectedTabIndex, tabSlug } = payload;

      const segment = WIDGETS_GROUP_SELECTION_TAB_MAP[tabSlug];

      return {
        ...state,
        [segment]: {
          ...state[segment],
          selectedTabIndex,
        },
      };
    }
    case actions.GROUP_SELECTION_TABS_LIST_SET: {
      const { tabList, tabSlug } = payload;

      const segment = WIDGETS_GROUP_SELECTION_TAB_MAP[tabSlug];

      return {
        ...state,
        [segment]: { ...state[segment], tabList },
      };
    }
    case actions.GROUP_SELECTION_TABS_LIST_LOADING_SET: {
      const { isLoading, tabSlug } = payload;

      const segment = WIDGETS_GROUP_SELECTION_TAB_MAP[tabSlug];

      return {
        ...state,
        [segment]: { ...state[segment], isLoading },
      };
    }
    default:
      return state;
  }
};
